import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLightbulb, faWandMagicSparkles, faSpinner, faChevronDown, faChevronUp,
  faHistory, faArrowLeft,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./SelfInsightsModal.module.css";
import ModalHeader from "./ModalHeader";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";

const WEEK_OPTIONS = [2, 4, 6, 8, 12];

function fmtDt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", {
      day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
}

/**
 * SelfInsightsModal — versão self-service (Plus) dos "Insights de IA" que o
 * professor já tem em TeacherAnalyticsModal.jsx. Mesmo backend
 * (POST/GET /ai/student-insights*), mesma regra de cooldown (7 dias) — só
 * que aqui o alvo é sempre o próprio usuário logado, nunca um student_id
 * escolhido. Ver app/api/ai_insights.py (_resolve_owner) para a permissão.
 */
export default function SelfInsightsModal({ open, onClose, user }) {
  useBodyScrollLock(open);

  const [view, setView] = useState("main"); // main | confirm | history
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [entry, setEntry] = useState(null); // { text, generatedAt, nextAvailableAt, weeks }
  const [weeks, setWeeks] = useState(6);
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState("");
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);

  useEffect(() => {
    if (!open) {
      setView("main");
      setExpanded(false);
      setGenError("");
      setHistory([]);
      setHistoryLoaded(false);
      setExpandedHistoryId(null);
      return;
    }
    if (!user?.id) return;
    setLoading(true);
    setLoadError("");
    api.get(`/ai/student-insights/${user.id}`)
      .then(res => {
        setEntry(res.data ? {
          text: res.data.insights,
          generatedAt: res.data.generated_at,
          nextAvailableAt: res.data.next_available_at,
          weeks: res.data.period_weeks,
        } : null);
      })
      .catch(err => setLoadError(err.response?.data?.detail || "Erro ao carregar insights."))
      .finally(() => setLoading(false));
  }, [open, user?.id]);

  const nextTs = entry?.nextAvailableAt ? new Date(entry.nextAvailableAt) : null;
  const canGenerate = !nextTs || Date.now() >= nextTs.getTime();

  async function handleGenerate() {
    setGenerating(true);
    setGenError("");
    try {
      const { data } = await api.post("/ai/student-insights", { student_id: user.id, weeks });
      setEntry({
        text: data.insights,
        generatedAt: data.generated_at,
        nextAvailableAt: data.next_available_at,
        weeks,
      });
      setExpanded(true);
      setView("main");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429 && detail?.next_available_at) {
        setEntry(prev => ({ ...(prev || {}), nextAvailableAt: detail.next_available_at }));
        setView("main");
        setGenError(detail.message || "Limite semanal atingido.");
      } else {
        setGenError(typeof detail === "string" ? detail : "Erro ao gerar insights.");
      }
    } finally {
      setGenerating(false);
    }
  }

  async function openHistory() {
    setView("history");
    if (historyLoaded) return;
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/ai/student-insights/${user.id}/history`);
      setHistory(data || []);
    } catch (_) {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
      setHistoryLoaded(true);
    }
  }

  if (!open) return null;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <ModalHeader onClose={onClose} disabled={generating} />
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.icon}><FontAwesomeIcon icon={faLightbulb} /></span>
            <div>
              <h2 className={styles.title}>Análise da minha evolução com IA</h2>
              <p className={styles.sub}>Aderência, volume e progresso do seu treino</p>
            </div>
          </div>
        </div>

        <div className={styles.body}>
          {loading ? (
            <div className={styles.loadingMsg}>Carregando…</div>
          ) : loadError ? (
            <div className={styles.error}>{loadError}</div>

          ) : view === "confirm" ? (
            <div className={styles.confirmView}>
              <p>
                A IA vai analisar <strong>{weeks} semanas</strong> do seu histórico e gerar insights
                acionáveis sobre seu treino e evolução.
              </p>
              {entry && <p>O insight atual (gerado em {fmtDt(entry.generatedAt)}) será substituído.</p>}
              <div className={styles.limitBox}>
                <FontAwesomeIcon icon={faLightbulb} />
                <span>1 geração a cada 7 dias.{!entry && " Após gerar, a próxima estará disponível em 7 dias."}</span>
              </div>
              {genError && <div className={styles.error}>{genError}</div>}
              <div className={styles.confirmActions}>
                <button className={styles.btnSecondary} onClick={() => setView("main")} disabled={generating}>
                  Cancelar
                </button>
                <button className={styles.btnPrimary} onClick={handleGenerate} disabled={generating}>
                  {generating ? (
                    <><FontAwesomeIcon icon={faSpinner} spin /> Analisando…</>
                  ) : (
                    entry ? "Regenerar" : "Gerar agora"
                  )}
                </button>
              </div>
            </div>

          ) : view === "history" ? (
            <div className={styles.historyView}>
              <button type="button" className={styles.backLink} onClick={() => setView("main")}>
                <FontAwesomeIcon icon={faArrowLeft} /> Voltar
              </button>
              {historyLoading ? (
                <div className={styles.loadingMsg}>Carregando…</div>
              ) : history.length === 0 ? (
                <div className={styles.emptyMsg}>Nenhum insight registrado ainda.</div>
              ) : (
                history.map(item => (
                  <div key={item.id} className={styles.historyRow}>
                    <button
                      type="button"
                      className={styles.historyRowHeader}
                      onClick={() => setExpandedHistoryId(id => (id === item.id ? null : item.id))}
                    >
                      <span>{fmtDt(item.generated_at)} · {item.weeks_analyzed} semanas</span>
                      <FontAwesomeIcon icon={expandedHistoryId === item.id ? faChevronUp : faChevronDown} />
                    </button>
                    {expandedHistoryId === item.id && (
                      <div className={styles.historyRowBody}>{item.insights_text}</div>
                    )}
                  </div>
                ))
              )}
            </div>

          ) : (
            <div className={styles.mainView}>
              <div className={styles.weeksRow}>
                <label className={styles.weeksLabel}>
                  Analisar
                  <select value={weeks} onChange={e => setWeeks(Number(e.target.value))} className={styles.weeksSelect}>
                    {WEEK_OPTIONS.map(w => <option key={w} value={w}>{w} semanas</option>)}
                  </select>
                </label>
                <button
                  type="button"
                  className={styles.btnPrimarySmall}
                  onClick={() => setView("confirm")}
                  disabled={!canGenerate}
                  title={!canGenerate ? `Próxima geração disponível em ${fmtDt(entry?.nextAvailableAt)}` : ""}
                >
                  <FontAwesomeIcon icon={faWandMagicSparkles} /> {entry ? "Regenerar" : "Gerar insights"}
                </button>
              </div>

              <div className={styles.limitInfo}>
                {entry && canGenerate && `Última geração: ${fmtDt(entry.generatedAt)}`}
                {entry && !canGenerate && (
                  <span className={styles.warning}>
                    ⏳ Limite semanal atingido - próxima geração em {fmtDt(entry.nextAvailableAt)}
                  </span>
                )}
                {!entry && "1 geração por semana."}
              </div>

              {entry?.text ? (
                <>
                  {expanded && <div className={styles.insightText}>{entry.text}</div>}
                  <div className={styles.bottomBar}>
                    <span className={styles.meta}>Gerado em {fmtDt(entry.generatedAt)} · {entry.weeks} semanas analisadas</span>
                    <div className={styles.bottomActions}>
                      <button type="button" className={styles.linkBtn} onClick={() => setExpanded(v => !v)}>
                        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} /> {expanded ? "Recolher" : "Ver tudo"}
                      </button>
                      <button type="button" className={styles.linkBtn} onClick={openHistory}>
                        <FontAwesomeIcon icon={faHistory} /> Histórico
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.emptyMsg}>
                  Você ainda não gerou nenhum insight. Clique em "Gerar insights" pra receber uma
                  análise da sua evolução.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
